import { createEnum } from '../helpers'
import { Channel } from './materials'

// Business-rule failures on a tax or pricing assumption entry, reported as a custom issue's `params.type`
export const ASSUMPTION_DETAIL_ERRORS = ['variantRequiresBrand'] as const
export const ASSUMPTION_DETAIL_ERROR_TYPES = createEnum(ASSUMPTION_DETAIL_ERRORS)

export const TAX_ASSUMPTION_TYPE_VALUES = [
  'federalExcise',
  'provincialTobaccoTax',
  'interimValueAddedTax',
  'finalValueAddedTax',
] as const

export const TAX_ASSUMPTION_TYPES = createEnum(TAX_ASSUMPTION_TYPE_VALUES)
export type TaxAssumptionType = (typeof TAX_ASSUMPTION_TYPE_VALUES)[number]

export function parseTaxAssumptionType(type: string): TaxAssumptionType {
  if (TAX_ASSUMPTION_TYPE_VALUES.includes(type as TaxAssumptionType)) {
    return type as TaxAssumptionType
  }

  throw new Error(`Invalid tax assumption type: ${type}`)
}

export const PRICING_RATE_TYPE_VALUES = [
  'rrp',
  'federalExcise',
  'ptt',
  'directPtt',
  'indirectPtt',
  'interimVat',
  'finalVat',
  'wholesaleMix',
] as const

export const PRICING_RATE_TYPES = createEnum(PRICING_RATE_TYPE_VALUES)
export type PricingRateType = (typeof PRICING_RATE_TYPE_VALUES)[number]

export function isPricingRateTypePercentage(type: PricingRateType): boolean {
  return type === 'interimVat' || type === 'finalVat' || type === 'wholesaleMix'
}

// Map of pricing rate types that require channel filtering
export const PRICING_RATE_TYPE_CHANNEL_FILTERS = new Map<PricingRateType, Set<Channel>>([
  ['directPtt', new Set<Channel>(['DSS'])],
  ['indirectPtt', new Set<Channel>(['MW', 'WH'])],
])

const PRICING_RATE_STATUS_INVALID = [
  'INVALID_CHANNEL_MIX_NOT_FOUND',
  'INVALID_CHANNEL_MIX_NOT_COMPLETE',
] as const
const PRICING_RATE_STATUS_WARNING = ['WARNING_CHANNEL_MIX_TOTAL_ZERO'] as const
export const PRICING_RATE_STATUS_VALUES = [
  ...PRICING_RATE_STATUS_INVALID,
  ...PRICING_RATE_STATUS_WARNING,
] as const

export const PRICING_RATE_STATUSES = createEnum(PRICING_RATE_STATUS_VALUES)
export type PricingRateStatus = (typeof PRICING_RATE_STATUS_VALUES)[number]

export function isPricingRateStatusInvalid(status: PricingRateStatus): boolean {
  return (PRICING_RATE_STATUS_INVALID as unknown as PricingRateStatus[]).includes(status)
}

export const FEDERAL_EXCISE_IMPORT_ERRORS = [
  'zodSchemaError',
  'invalidProductCategory',
  'invalidKey',
  'invalidMarkingCode',
  'negativeAmount',
] as const

export const FEDERAL_EXCISE_IMPORT_ERROR_TYPES = createEnum(FEDERAL_EXCISE_IMPORT_ERRORS)
export type FederalExciseImportErrorType = (typeof FEDERAL_EXCISE_IMPORT_ERRORS)[number]

/** `PATCH /crt/federal-excise` rejects a key that is not a candidate of its product category. */
export const FEDERAL_EXCISE_MAPPING_ERRORS = ['unknownKey'] as const
export const FEDERAL_EXCISE_MAPPING_ERROR_TYPES = createEnum(FEDERAL_EXCISE_MAPPING_ERRORS)
export type FederalExciseMappingErrorType = (typeof FEDERAL_EXCISE_MAPPING_ERRORS)[number]

export const RETAILER_RATES_IMPORT_ERRORS = ['zodSchemaError', 'invalidCategoryProvince'] as const

export const RETAILER_RATES_IMPORT_ERROR_TYPES = createEnum(RETAILER_RATES_IMPORT_ERRORS)
export type RetailerRatesImportErrorType = (typeof RETAILER_RATES_IMPORT_ERRORS)[number]

export const CONSUMER_RATES_IMPORT_ERRORS = ['zodSchemaError', 'invalidCategoryProvince'] as const

export const CONSUMER_RATES_IMPORT_ERROR_TYPES = createEnum(CONSUMER_RATES_IMPORT_ERRORS)
export type ConsumerRatesImportErrorType = (typeof CONSUMER_RATES_IMPORT_ERRORS)[number]

export const VAT_CATEGORIES = ['Cigarettes', 'Vaping', 'Other Products'] as const

export const VAT_CATEGORY_VALUES = createEnum(VAT_CATEGORIES)
export type VatCategory = (typeof VAT_CATEGORIES)[number]
